import { REQUIRED_MESSAGES } from '@srm/ui/lib/requiredMessage';
import React, { useEffect, useState } from 'react';
import DueTimeModeTypeOption, { DueTimeModeType } from '@/types/DueTimeType';
import { RouteMapExecutionService } from '@services/actions/RouteMapExecutionService';
import { getABookFormValues } from '@utils/abookUtils';
import { IRouteMapActionResult } from '@models/document/routeMapActionsExecutors/IRouteMapActionModel';
import DurationSpanTypeOption from '@/types/DurationSpanType';
import { useForm } from 'react-hook-form';
import { IOption } from '@srm/ui/types/IOption';
import ModalContainer, { IModalContainerProps } from '@molecules/ModalContainer/ModalContainer';
import { useActionSubmit } from '@organisms/actions/useActionSubmit';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';
import AbookpickerControl from '@controls/AbookpickerControl';
import { IAddressBookData } from '@models/addressbook/IAddressBookData';
import SelectControl from '@srm/ui/controls/SelectControl';
import InputNumberControl from '@srm/ui/controls/InputNumberControl';
import DateFieldControl from '@srm/ui/controls/DateFieldControl';
import TextareaControl from '@srm/ui/controls/TextareaControl';
import { requiredTextRules } from '@srm/ui/lib/requiredTextRules';
import { IEditRouteMapFormData } from './IEditRouteMapFormData';
import { IEditRouteMapProps } from './IEditRouteMapProps';
import { useDocumentAbookTabs } from '@/hooks/useDocumentAbookTabs';

export const EditRouteMapModal: React.FC<IEditRouteMapProps> = (props: IEditRouteMapProps) => {
    const abookTabs = useDocumentAbookTabs(props.documentKey, props.abookOptions?.tabs);
    const [periodType, setPeriodType] = useState<DueTimeModeType>(
        DueTimeModeType[props.dueTimeMode as keyof typeof DueTimeModeType],
    );
    const [executorDisplayValue, setExecutorDisplayValue] = useState<string>(props.executorDisplayName);
    const [formValues, setFormValues] = useState<string>('{}');
    const activated = React.useRef(false);

    const serv = new RouteMapExecutionService();

    const { errorText, isBusy, setIsBusy, isSubmitting, okClick, cancelClick } = useActionSubmit({
        completeHandler: props.completeHandler,
        initialBusy: true,
        submit: (result: IRouteMapActionResult) =>
            serv.edit(props.actionKey, props.activityKey, props.processKey, props.documentKey, result),
    });

    const getFormValues = async () => {
        const vals = await getABookFormValues(props.documentKey, props.abookOptions?.formValues);
        if (activated.current && vals) {
            setFormValues(vals);
        }
    };

    useEffect(() => {
        // Закрытое окно ответ подготовки не применяет: getFormValues сверяется с activated.
        let ignore = false;
        activated.current = true;
        setIsBusy(true);
        getFormValues()
            .catch((error: unknown) => console.error(error))
            .finally(() => {
                if (!ignore) setIsBusy(false);
            });
        return () => {
            ignore = true;
            activated.current = false;
        };
    }, []);

    const onSubmit = (form: IEditRouteMapFormData) => {
        const result = {
            levelKey: props.levelKey,
            nextlevelKey: props.nextlevelKey,
            stepName: '',
            executorsIds: form.executors.split('|').map((n) => parseInt(n, 10)),
            dueTimeMode: (form.periodType[0] ?? DueTimeModeTypeOption[0]).value,
            transitionType: '',
            activityKey: props.activityKey,
            tasktext: form.tasktext,
        } as IRouteMapActionResult;

        if (periodType == DueTimeModeType.Date) {
            result.periodDate = form.periodDate;
        }

        if (periodType == DueTimeModeType.TimeSpan) {
            result.termType = (form.termType[0] ?? DurationSpanTypeOption[0]).value;
            result.termCount = form.termCount ?? 1;
        }

        return okClick(result);
    };

    const formState = useForm<IEditRouteMapFormData>({
        mode: 'onBlur', // "onChange"
    });
    const hasUnsavedInput = useUnsavedFormInput(formState, { textFields: ['tasktext'] });

    useEffect(() => {
        const periodType = [
            DueTimeModeTypeOption.filter((x) => x.value.toLowerCase() == props.dueTimeMode.toLowerCase())[0] ??
                DueTimeModeTypeOption[0],
        ];
        const termType = [
            DurationSpanTypeOption.filter((x) => x.value.toLowerCase() == props.termType.toLowerCase())[0] ??
                DurationSpanTypeOption[0],
        ];

        formState.reset({
            executors: props.executorId,
            periodType: periodType,
            termCount: props.termCount,
            termType: termType,
            periodDate: props.periodDate,
            tasktext: props.tasktext,
        });
    }, []);

    const onPeriodTypeChange = (option: IOption[]) => {
        if (option.length == 0) return;
        const type = DueTimeModeType[option[0].value as keyof typeof DueTimeModeType];
        setPeriodType(type);
    };

    const isPeriodDateRequired = periodType == DueTimeModeType.Date;

    const modalContainerProps: IModalContainerProps = {
        header: 'Редактирование этапа',
        size: props.modalSize,
        showOkButton: true,
        showCancelButton: true,
        loading: isBusy,
        closeDisabled: isSubmitting,
        hasUnsavedInput,
        onCancel: cancelClick,
        errorText: errorText,
        formMethods: formState,
        onSubmit: onSubmit,
        okButtonDisabled: false,
    };

    return (
        <ModalContainer {...modalContainerProps}>
            <AbookpickerControl
                label="Исполнитель"
                placeholder="Выберите исполнителя"
                isMultiple={false}
                title="Исполнитель"
                displayValue={executorDisplayValue}
                formValues={formValues}
                name="executors"
                tabsSettings={abookTabs}
                externalSearch={props.abookOptions?.externalSearch}
                required={true}
                rules={{ required: REQUIRED_MESSAGES.select }}
                control={formState.control}
                showChips={false}
                onSetValues={(value: IAddressBookData[]) => {
                    setExecutorDisplayValue(value?.map((x) => x.fullName)?.join(','));
                }}
            />

            <SelectControl
                name="periodType"
                label="Срок исполнения"
                multiselect={false}
                readOnly
                required={true}
                rules={{ required: REQUIRED_MESSAGES.select }}
                placeholder="Укажите срок исполнения"
                options={DueTimeModeTypeOption}
                loading={isBusy}
                control={formState.control}
                onValueChange={onPeriodTypeChange}
            />

            {periodType == DueTimeModeType.TimeSpan && (
                <>
                    <InputNumberControl
                        name="termCount"
                        defaultValue={props.termCount}
                        min={1}
                        max={9999}
                        floatPoints={0}
                        required={true}
                        rules={{ required: REQUIRED_MESSAGES.number }}
                        control={formState.control}
                    />

                    <SelectControl
                        name="termType"
                        multiselect={false}
                        readOnly
                        required={true}
                        rules={{ required: REQUIRED_MESSAGES.select }}
                        options={DurationSpanTypeOption}
                        loading={isBusy}
                        control={formState.control}
                        onValueChange={() => {}}
                    />
                </>
            )}

            {periodType == DueTimeModeType.Date && (
                <DateFieldControl
                    name="periodDate"
                    required={isPeriodDateRequired}
                    rules={isPeriodDateRequired ? { required: REQUIRED_MESSAGES.date } : undefined}
                    control={formState.control}
                />
            )}

            <TextareaControl
                label="Текст задачи"
                readOnly={false}
                required={true}
                rules={requiredTextRules()}
                name="tasktext"
                control={formState.control}
            />
        </ModalContainer>
    );
};
